import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { Reveal, Eyebrow, PageBand, CTABand, Photo, GoldRule, scrollToId } from "@/components/Shared";
import { TREATMENTS } from "@/data/content";
import { PAGE_HEROES, TREATMENT_PHOTOS } from "@/data/photos";
import { ArrowRight } from "lucide-react";

const TreatmentsHub = () => (
  <>
    <SEO title="Treatments | Lumière Aesthetics, Cottesloe" description="Injectables, threads and regenerative, skin and laser, hair restoration, body and core-to-floor, minor procedures, bridal and Lumière Private — every treatment explained plainly." />
    <PageBand
      eyebrow="Treatments"
      title="Every treatment, explained plainly"
      standfirst="What it is, who it suits, what it costs or why it can't be priced publicly — and the risks, stated before anything else."
      image={PAGE_HEROES.treatments}
    />

    <nav aria-label="Jump to a treatment" className="bg-warm-white border-b border-hairline/60" data-testid="treatments-jump-nav">
      <div className="shell py-6 flex flex-wrap items-center gap-3">
        <span className="text-ink/50 text-xs tracking-[0.2em] uppercase mr-2">Jump to</span>
        {TREATMENTS.map((t) => (
          <a
            key={t.slug}
            href={`#treatment-${t.slug}`}
            onClick={(e) => { e.preventDefault(); scrollToId(`treatment-${t.slug}`, -110); }}
            data-testid={`jump-link-${t.slug}`}
            className="rounded-full border border-deep-sea/20 px-5 py-2 text-sm text-deep-sea transition-colors duration-300 hover:border-gold hover:bg-deep-sea hover:text-cream"
          >
            {t.title}
          </a>
        ))}
      </div>
    </nav>

    <section className="bg-warm-white" data-testid="treatments-grid">
      <div className="shell py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TREATMENTS.map((t, i) => (
            <Reveal key={t.slug} delay={(i % 2) * 0.08}>
              <Link
                to={`/treatments/${t.slug}`}
                id={`treatment-${t.slug}`}
                data-testid={`treatment-card-${t.slug}`}
                className="card-float overflow-hidden group h-full grid sm:grid-cols-5 scroll-mt-32"
              >
                <div className="sm:col-span-2">
                  <Photo photo={TREATMENT_PHOTOS[t.photo]} className="aspect-[4/3] sm:aspect-auto sm:h-full !rounded-none" width={800} />
                </div>
                <div className="sm:col-span-3 p-8">
                  <Eyebrow>{t.family}</Eyebrow>
                  <h3 className="text-deep-sea mt-2 group-hover:text-sea-green transition-colors duration-300">{t.title}</h3>
                  <p className="text-ink/70 text-sm mt-3 leading-relaxed">{t.short}</p>
                  <span className="inline-flex items-center gap-2 mt-5 text-sea-green group-hover:text-gold text-[11px] tracking-[0.18em] uppercase font-medium transition-colors duration-300">
                    Learn more
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 text-center max-w-2xl mx-auto">
          <GoldRule className="mx-auto mb-8" />
          <p className="text-ink/75 leading-[1.8]">
            Prescription treatments are never named or priced publicly, as Australian advertising regulations require.
            Your doctor gives you a written quote in consultation, before anything is booked.
          </p>
        </Reveal>
      </div>
    </section>

    <CTABand />
  </>
);

export default TreatmentsHub;
